import { useState, useEffect, useCallback } from 'react'
import { Copy, Check, Loader2, ExternalLink, AlertCircle } from 'lucide-react'
import { formatPrice, generateBalancePaymentToken } from '@limo/shared'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { supabase } from '@/lib/supabase'
import { getPublicSiteUrl } from '@/lib/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * Query builder for `booking_balance_links`.
 *
 * The generated Supabase types come from `npm run db:types` against the linked
 * project, so until this table lands there the typed client rejects the relation
 * name itself — not just its columns. One cast, in one place, to delete once the
 * types are regenerated. Mirrors `balanceLinksTable` in the viewer.
 */
const balanceLinks = () => (supabase as SupabaseClient).from('booking_balance_links')

/**
 * Hand the customer a link that pays off what they still owe.
 *
 * Modelled on the operator set-price page's success state: the operator gets the
 * URL, copies it, and decides when to send it. Deliberately NOT auto-sent —
 * operators routinely want to add their own words, or wait until after a phone
 * call, and a link that fires the moment you generate it takes that away.
 *
 * The row is written straight through Supabase rather than via an API route,
 * because RLS on `booking_balance_links` already scopes writes to the operator's
 * own account — the same authorization a hand-rolled route would have had to
 * re-implement. The customer-facing side is what needs a service-role route, and
 * has one.
 */

export interface GenerateBalanceLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The booking the link settles. For a paired round trip this must be the
   * OUTBOUND leg — one link covers the pair, and the viewer resolves the
   * combined balance from it.
   */
  bookingId: string
  accountId: string
  accountSlug: string
  accountCustomDomain?: string | null
  /** Combined across both legs for a round trip. */
  balanceDue: number
  /** Used to expire the link a day after the trip has run. */
  pickupDatetime?: string | null
  onGenerated?: () => void
}

interface GeneratedLink {
  url: string
  amountDue: number
  expiresAt: string | null
}

export function GenerateBalanceLinkDialog({
  open,
  onOpenChange,
  bookingId,
  accountId,
  accountSlug,
  accountCustomDomain,
  balanceDue,
  pickupDatetime,
  onGenerated,
}: GenerateBalanceLinkDialogProps) {
  const [link, setLink] = useState<GeneratedLink | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // A link generated for a previous booking must not linger in the dialog when
  // it is reopened somewhere else.
  useEffect(() => {
    if (!open) {
      setLink(null)
      setError(null)
      setCopied(false)
    }
  }, [open])

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true)
    setError(null)

    try {
      // Void any existing live link first, so only the newest is payable. A
      // customer holding an old link should find it dead rather than quietly pay
      // through one the operator thought they had replaced.
      const { error: voidError } = await balanceLinks()
        .update({ status: 'void' } as never)
        .eq('booking_id', bookingId)
        .eq('status', 'active')

      if (voidError) throw voidError

      const token = generateBalancePaymentToken()

      // A day past pickup. Once the trip has run and been settled at the kerb, a
      // still-live payment link is only a standing credential nobody needs.
      const expiresAt = pickupDatetime
        ? new Date(new Date(pickupDatetime).getTime() + 24 * 60 * 60 * 1000).toISOString()
        : null

      const { error: insertError } = await balanceLinks().insert({
        token,
        booking_id: bookingId,
        account_id: accountId,
        amount_due: balanceDue,
        status: 'active',
        expires_at: expiresAt,
      } as never)

      if (insertError) throw insertError

      // Prefer the account's own domain: a payment link on a stranger's domain
      // is exactly what customers are taught not to click.
      const baseUrl = accountCustomDomain
        ? `https://${accountCustomDomain}`
        : getPublicSiteUrl(accountSlug)

      setLink({ url: `${baseUrl}/pay/${token}`, amountDue: balanceDue, expiresAt })
      onGenerated?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the payment link')
    } finally {
      setIsGenerating(false)
    }
  }, [bookingId, accountId, accountSlug, accountCustomDomain, balanceDue, pickupDatetime, onGenerated])

  const handleCopy = useCallback(async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setError('Could not copy — select the link and copy it manually.')
    }
  }, [link])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Payment Link</DialogTitle>
          <DialogDescription>
            {link
              ? 'Send this to the customer. It pays the outstanding balance and nothing else.'
              : `Create a link the customer can use to pay the ${formatPrice(balanceDue)} still owed on this booking.`}
          </DialogDescription>
        </DialogHeader>

        {!link ? (
          <div className="space-y-4">
            <div className="rounded-lg border p-4 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Balance due</span>
              <span className="text-lg font-bold">{formatPrice(balanceDue)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Generating a new link cancels any previous one for this booking, so only the
              newest link can be paid.
            </p>
            {error && (
              <p className="text-sm text-destructive flex items-start gap-1.5">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {error}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input readOnly value={link.url} className="font-mono text-xs" />
              <Button type="button" variant="outline" size="icon" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button type="button" variant="outline" size="icon" asChild>
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              For {formatPrice(link.amountDue)}
              {link.expiresAt &&
                ` · expires ${new Date(link.expiresAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}`}
              . The amount is re-checked when the customer opens it, so if you take payment
              another way first the link will simply say there is nothing to pay.
            </p>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          {!link ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={isGenerating}>
                {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Link
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
